import type { Board, GridPos, Piece } from "./board";
import { damageForTier, elementMultiplier, hashSeed, hpForTier, Rng } from "./balance";

export type Side = "player" | "opponent";
export type Winner = Side | "draw";
export type UnitId = { side: Side; pieceId: string };
export type BattleUnit = UnitId & { piece: Piece; pos: GridPos; maxHp: number; hp: number; charges: number };
export type BattleEvent =
  | { type: "attack"; at: number; actor: UnitId; target: UnitId; affected: UnitId[]; area: GridPos[]; element: Piece["element"]; tier: number; impactAt: number }
  | { type: "charged"; at: number; actor: UnitId; element: Piece["element"] }
  | { type: "damage"; at: number; target: UnitId; amount: number; hp: number; maxHp: number }
  | { type: "defeated"; at: number; target: UnitId }
  | { type: "ended"; at: number; winner: Winner };
export type BattleProgress = { elapsed: number; playerInputs: number[]; opponentInputs?: number[]; commands?: { side: Side; at: number }[] };
export type Battle = { units: BattleUnit[]; events: BattleEvent[]; winner: Winner; duration: number; rngState: number };
export const unitKey = (id: UnitId): string => `${id.side}:${id.pieceId}`;
const idOf = (unit: UnitId): UnitId => ({ side: unit.side, pieceId: unit.pieceId });
const LIMIT = 90_000;
export const CHARGE_CAPACITY = 5;
const STARTING_CHARGES = 2;
const TRAVEL = 330;
type Actor = BattleUnit & { rng: Rng; nextCharge: number; refillMs: number; nextDecision: number; readyAt: number; shotSpacing: number };
type Shot = { at: number; actor: Actor };
type Hit = { at: number; target: Actor; damage: number };

export const attackRadius = (tier: number): number => tier >= 6 ? 2 : tier >= 5 ? 1 : 0;
export function attackCells(center: GridPos, tier: number, board: Board): GridPos[] {
  const radius = attackRadius(tier), cells: GridPos[] = [];
  for (let y = Math.max(0, center.y - radius); y <= Math.min(board.height - 1, center.y + radius); y++) {
    for (let x = Math.max(0, center.x - radius); x <= Math.min(board.width - 1, center.x + radius); x++) cells.push({ x, y });
  }
  return cells;
}

// An event-driven clock keeps outcomes identical across frame rates and replay.
// Player commands reserve credits; Phaser never decides damage or the winner.
export class BattleEngine {
  private actors: Actor[] = [];
  private shots: Shot[] = [];
  private hits: Hit[] = [];
  private commandRng: Rng;
  private closing = false;
  private endAt = Infinity;
  private inputs: number[] = [];
  private opponentInputs: number[] = [];
  private commands: { side: Side; at: number }[] = [];
  time = 0;
  winner: Winner | null = null;
  readonly rngState: number;

  constructor(private player: Board, private opponent: Board, seed: number, private autoPlayer = false, private autoOpponent = true) {
    this.commandRng = new Rng(hashSeed("player-commands", seed));
    this.rngState = new Rng(hashSeed("next-battle", seed)).next() * 4294967296;
    for (const [side, board] of [["player", player], ["opponent", opponent]] as const) {
      const ids = new Set<string>();
      board.cells.forEach((piece, index) => {
        if (!piece) return;
        if (ids.has(piece.id)) throw new Error("Piece IDs must be unique within a team");
        ids.add(piece.id);
        const pos = { x: index % board.width, y: Math.floor(index / board.width) };
        const rng = new Rng(hashSeed(`${side}:${piece.id}:${pos.x},${pos.y}`, seed));
        const refillMs = 1000 + Math.floor(rng.next() * 700);
        const nextCharge = Math.round(refillMs * (0.4 + rng.next() * 0.6));
        const readyAt = Math.floor(rng.next() * 300);
        const shotSpacing = 330 + Math.floor(rng.next() * 180);
        this.actors.push({ side, pieceId: piece.id, piece: { ...piece }, pos,
          maxHp: hpForTier(piece.tier), hp: hpForTier(piece.tier), rng, charges: STARTING_CHARGES, nextCharge,
          refillMs, shotSpacing,
          nextDecision: 350 + Math.floor(rng.next() * 450), readyAt });
      });
    }
    this.checkClosing();
  }
  get units(): BattleUnit[] {
    return this.actors.map(({ side, pieceId, piece, pos, hp, maxHp, charges }) =>
      ({ side, pieceId, piece: { ...piece }, pos: { ...pos }, hp, maxHp, charges: hp > 0 ? charges : 0 }));
  }
  get progress(): BattleProgress { return { elapsed: this.time, playerInputs: this.inputs.slice(), opponentInputs: this.opponentInputs.slice(), commands: this.commands.slice() }; }
  get canPlayerAttack(): boolean {
    return !this.closing && this.living("player").some(actor => actor.charges >= 1);
  }
  private living(side: Side): Actor[] { return this.actors.filter(actor => actor.side === side && actor.hp > 0); }
  private automatic(actor: Actor): boolean { return actor.side === "opponent" ? this.autoOpponent : this.autoPlayer; }
  private refill(at: number): void {
    this.time = at;
  }
  private reserve(actor: Actor, offset = 0): void {
    actor.charges--;
    if (actor.nextCharge === Infinity) actor.nextCharge = this.time + actor.refillMs;
    const at = Math.max(this.time, actor.readyAt) + offset;
    actor.readyAt = at + actor.shotSpacing;
    this.shots.push({ at, actor });
  }
  requestPlayerAttack(): number {
    return this.requestAttack("player");
  }
  requestOpponentAttack(): number {
    return this.requestAttack("opponent");
  }
  private requestAttack(side: Side): number {
    if (this.closing) return 0;
    const alive = this.living(side);
    const eligible = alive.filter(actor => actor.charges >= 1);
    const count = Math.min(eligible.length, Math.max(1, Math.ceil(alive.length / 3)));
    const gap = Math.min(75, 220 / Math.max(1, count - 1));
    for (let i = 0; i < count; i++) {
      // Rotate through rested friends, with random choice within that group.
      // Two starting credits must not make the same few units hog every click.
      const earliest = Math.min(...eligible.map(actor => actor.readyAt));
      const rested = eligible.filter(actor => actor.readyAt <= earliest + 90);
      const actor = rested[Math.floor(this.commandRng.next() * rested.length)];
      const index = eligible.indexOf(actor);
      this.reserve(eligible.splice(index, 1)[0], i ? Math.round(i * gap + this.commandRng.next() * 15) : 0);
    }
    if (count) {
      (side === "player" ? this.inputs : this.opponentInputs).push(this.time);
      this.commands.push({ side, at: this.time });
    }
    return count;
  }
  restore(progress: BattleProgress): void {
    if (this.time !== 0 || this.commands.length) throw new Error("Restore requires a fresh battle");
    const commands = progress.commands ?? [...progress.playerInputs.map(at => ({ at, side: "player" as const })), ...(progress.opponentInputs ?? []).map(at => ({ at, side: "opponent" as const }))]
      .sort((a,b) => a.at-b.at || (a.side === "player" ? -1 : 1));
    for (const command of commands) { this.advance(command.at); this.requestAttack(command.side); }
    this.advance(progress.elapsed);
  }
  advance(to: number): BattleEvent[] {
    if (!Number.isFinite(to) || to < this.time) throw new Error("Battle time must move forward");
    const events: BattleEvent[] = [];
    if (this.winner !== null) return events;
    while (this.winner === null) {
      const next = Math.min(this.endAt,
        this.closing ? Infinity : LIMIT,
        ...this.hits.map(hit => hit.at),
        ...(this.closing ? [] : this.shots.map(shot => shot.at)),
        ...(this.closing ? [] : this.actors.filter(actor => actor.hp > 0).map(actor => actor.nextCharge)),
        ...(this.closing ? [] : this.actors.filter(actor => actor.hp > 0 && this.automatic(actor)).map(actor => actor.nextDecision)));
      if (next > to) { this.refill(to); break; }
      this.refill(next);
      const impacts = this.hits.filter(hit => hit.at === next);
      this.hits = this.hits.filter(hit => hit.at !== next);
      // All damage at this instant resolves together, including double knockouts.
      for (const actor of this.actors.filter(actor => actor.hp > 0)) {
        const damage = impacts.filter(hit => hit.target === actor).reduce((sum, hit) => sum + hit.damage, 0);
        if (!damage) continue;
        actor.hp = Math.max(0, actor.hp - damage);
        events.push({ type: "damage", at: next, target: idOf(actor), amount: damage, hp: actor.hp, maxHp: actor.maxHp });
        if (!actor.hp) {
          actor.charges = 0; actor.nextCharge = Infinity;
          events.push({ type: "defeated", at: next, target: idOf(actor) });
        }
      }
      this.checkClosing();
      if (next === this.endAt) {
        const player = this.living("player").length, opponent = this.living("opponent").length;
        this.winner = player && !opponent ? "player" : opponent && !player ? "opponent" : "draw";
        events.push({ type: "ended", at: next, winner: this.winner });
        break;
      }
      if (this.closing) continue;
      for (const actor of this.actors.filter(actor => actor.hp > 0 && actor.nextCharge === next)) {
        actor.charges = Math.min(CHARGE_CAPACITY, actor.charges + 1);
        actor.nextCharge = actor.charges < CHARGE_CAPACITY ? next + actor.refillMs : Infinity;
        events.push({ type: "charged", at: next, actor: idOf(actor), element: actor.piece.element });
      }
      for (const actor of this.actors.filter(actor => actor.hp > 0 && this.automatic(actor) && actor.nextDecision === next)) {
        if (actor.charges >= 1) this.reserve(actor);
        actor.nextDecision = next + 380 + Math.floor(actor.rng.next() * 640);
      }
      const launches = this.shots.filter(shot => shot.at === next);
      this.shots = this.shots.filter(shot => shot.at !== next);
      for (const { actor } of launches) {
        if (!actor.hp) continue;
        const targets = this.living(actor.side === "player" ? "opponent" : "player");
        if (!targets.length) continue;
        const target = targets[Math.floor(actor.rng.next() * targets.length)];
        const area = attackCells(target.pos, actor.piece.tier, target.side === "player" ? this.player : this.opponent);
        const affected = targets.filter(unit => area.some(pos => pos.x === unit.pos.x && pos.y === unit.pos.y));
        const impactAt = next + TRAVEL;
        events.push({ type: "attack", at: next, actor: idOf(actor), target: idOf(target), affected: affected.map(idOf), area,
          element: actor.piece.element, tier: actor.piece.tier, impactAt });
        for (const victim of affected) this.hits.push({ at: impactAt, target: victim,
          damage: Math.round(damageForTier(actor.piece.tier) * elementMultiplier(actor.piece.element, victim.piece.element)) });
      }
    }
    return events;
  }
  private checkClosing(): void {
    if (this.time >= LIMIT || !this.living("player").length || !this.living("opponent").length) {
      this.closing = true; this.shots = [];
      // Already-launched shots still land. A dead shooter can produce a fair draw.
      if (!this.hits.length && this.endAt === Infinity) this.endAt = this.time + 600;
    }
  }
}

// A symmetric autoplay harness for balance checks; the game uses live commands.
export function simulateBattle(player: Board, opponent: Board, seed: number): Battle {
  const engine = new BattleEngine(player, opponent, seed, true);
  const events = engine.advance(92_000);
  return { units: engine.units, events, winner: engine.winner!, duration: events.at(-1)!.at, rngState: engine.rngState };
}
